import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

// Define the possible values for dataset processing stages
export const DatasetProcessingStageEnum = z.enum([
  'parsing',
  'statistics',
  'indexing',
  'embeddings',
]);

export type DatasetProcessingStage = z.infer<typeof DatasetProcessingStageEnum>;

// Define the possible values for dataset processing status
export const DatasetProcessingStatusEnum = z.enum([
  'pending',
  'in_progress',
  'completed',
  'failed',
]);

export type DatasetProcessingStatus = z.infer<
  typeof DatasetProcessingStatusEnum
>;

export const DatasetProcessingStateSchema = z.object({
  datasetProcessingStateId: PrimaryKeySchema,
  datasetDataVersionId: PrimaryKeySchema,
  datasetSchemaVersionId: PrimaryKeySchema, // Schema version used for processing
  stage: DatasetProcessingStageEnum,
  status: DatasetProcessingStatusEnum,
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  results: z.record(z.any()).nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type DatasetProcessingState = z.infer<
  typeof DatasetProcessingStateSchema
>;

export const DatasetProcessingStatesSchema = z.array(
  DatasetProcessingStateSchema,
);

export type DatasetProcessingStates = z.infer<
  typeof DatasetProcessingStatesSchema
>;

export const DatasetProcessingStatesQuerySchema = z
  .object({
    datasetProcessingStateIds: commaDelimitedList(
      PrimaryKeySchema.array(),
    ).optional(),
    datasetDataVersionId: PrimaryKeySchema.optional(),
    stage: DatasetProcessingStageEnum.optional(),
    status: DatasetProcessingStatusEnum.optional(),
  })
  .merge(
    createOffsetPaginationRequestQuerySchema(
      z.enum(['createdAt', 'updatedAt']),
    ),
  );

export type DatasetProcessingStatesQuery = z.infer<
  typeof DatasetProcessingStatesQuerySchema
>;

export const CreateDatasetProcessingStateRequestSchema = z.object({
  datasetDataVersionId: PrimaryKeySchema,
  datasetSchemaVersionId: PrimaryKeySchema,
  stage: DatasetProcessingStageEnum,
  status: DatasetProcessingStatusEnum,
  startedAt: z.coerce.date().optional(),
  completedAt: z.coerce.date().optional(),
  results: z.record(z.any()).optional(),
  errorMessage: z.string().optional(),
});

export type CreateDatasetProcessingStateRequest = z.infer<
  typeof CreateDatasetProcessingStateRequestSchema
>;

export const UpdateDatasetProcessingStateRequestSchema = z.object({
  status: DatasetProcessingStatusEnum.optional(),
  startedAt: z.coerce.date().optional(),
  completedAt: z.coerce.date().optional(),
  results: z.record(z.any()).optional(),
  errorMessage: z.string().optional(),
});

export type UpdateDatasetProcessingStateRequest = z.infer<
  typeof UpdateDatasetProcessingStateRequestSchema
>;

export const UpsertDatasetProcessingStateRequestSchema = z.object({
  datasetDataVersionId: PrimaryKeySchema,
  datasetSchemaVersionId: PrimaryKeySchema,
  stage: DatasetProcessingStageEnum,
  status: DatasetProcessingStatusEnum,
  startedAt: z.coerce.date().optional(),
  completedAt: z.coerce.date().optional(),
  results: z.record(z.any()).optional(),
  errorMessage: z.string().optional(),
});

export type UpsertDatasetProcessingStateRequest = z.infer<
  typeof UpsertDatasetProcessingStateRequestSchema
>;

// Processing progress summary type
export const DatasetProcessingProgressSchema = z.object({
  datasetDataVersionId: PrimaryKeySchema,
  progressPercent: z.number().min(0).max(100),
  currentStage: DatasetProcessingStageEnum.nullable(),
  totalStages: z.number().int().positive(),
  completedStages: z.number().int().min(0),
  failedStages: z.number().int().min(0),
  stages: z.array(
    z.object({
      stage: DatasetProcessingStageEnum,
      status: DatasetProcessingStatusEnum,
      startedAt: z.coerce.date().nullable(),
      completedAt: z.coerce.date().nullable(),
      errorMessage: z.string().nullable(),
    }),
  ),
  isCompleted: z.boolean(),
  hasFailures: z.boolean(),
});

export type DatasetProcessingProgress = z.infer<
  typeof DatasetProcessingProgressSchema
>;
